import { RestError } from "@/models/client/RestResponse";

export const MAX_QUERY_RETRIES = 3;

/** Client errors worth another attempt: both are about timing, not about the request itself. */
const RETRYABLE_CLIENT_ERRORS = [408, 429];

const isSettledClientError = (status: number): boolean =>
  status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERRORS.includes(status);

/**
 * Whether react-query should try a failed query again.
 *
 * The default — three retries, no questions asked — turns one rejected request into four. A 4xx is
 * the API's settled answer (a missing permission, a missing entity, a malformed request), so
 * retrying it only multiplies the noise: the ~1.5k 403s a day on the group and widget-template
 * endpoints arrived in bursts averaging 4.4, one real mount each (LAR-456).
 *
 * Only errors carrying a status are judged, i.e. a {@link RestError}. Anything else (a network drop,
 * a parse failure) keeps the default behaviour, because it may well be transient.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  if (!(error instanceof RestError)) return true;

  // A synthetic 401 is the client failing to get a token, not the API rejecting the
  // user, and it recovers on its own — so it keeps its retries (LAR-361).
  if (error.clientAuthFailure !== undefined) return true;

  return !isSettledClientError(error.status);
};
